/**
 * S8 (Schedule 8) Controlled Drug Register Model
 * Compliance: Drugs, Poisons and Controlled Substances Act (VIC),
 * Health (Drugs and Poisons) Regulation (QLD), Poisons and Therapeutic Goods Act (NSW).
 * All S8 transactions require dual witness, immutable audit trail, and running balance reconciliation.
 */

export const S8_TRANSACTION_TYPE = {
  RECEIVED: 'received',
  ADMINISTERED: 'administered',
  WASTED: 'wasted',
  RETURNED: 'returned',
  DISCREPANCY: 'discrepancy',
  STOCK_CHECK: 'stock_check',
  TRANSFERRED_IN: 'transferred_in',
  TRANSFERRED_OUT: 'transferred_out',
};

export const S8_TRANSACTION_LABELS = {
  [S8_TRANSACTION_TYPE.RECEIVED]: 'Received from Pharmacy',
  [S8_TRANSACTION_TYPE.ADMINISTERED]: 'Administered to Resident',
  [S8_TRANSACTION_TYPE.WASTED]: 'Wasted / Destroyed',
  [S8_TRANSACTION_TYPE.RETURNED]: 'Returned to Pharmacy',
  [S8_TRANSACTION_TYPE.DISCREPANCY]: 'Discrepancy Noted',
  [S8_TRANSACTION_TYPE.STOCK_CHECK]: 'Stock Count / Audit',
  [S8_TRANSACTION_TYPE.TRANSFERRED_IN]: 'Transferred In',
  [S8_TRANSACTION_TYPE.TRANSFERRED_OUT]: 'Transferred Out',
};

export const S8_DISCREPANCY_RESOLUTION = {
  COUNTING_ERROR: 'counting_error',
  DOCUMENTATION_ERROR: 'documentation_error',
  BREAKAGE: 'breakage',
  THEFT_SUSPECTED: 'theft_suspected',
  UNRESOLVED: 'unresolved',
  OTHER: 'other',
};

export const createS8RegisterEntry = (data = {}) => {
  return {
    id: data.id || null,
    medicationOrderId: data.medicationOrderId || null,
    residentId: data.residentId || null,
    residentName: data.residentName || '',

    // Drug identification
    drugName: data.drugName || '',
    genericName: data.genericName || '',
    strength: data.strength || '',
    form: data.form || '',
    batchNumber: data.batchNumber || '',
    expiryDate: data.expiryDate || null,

    // Transaction
    transactionType: data.transactionType || S8_TRANSACTION_TYPE.ADMINISTERED,
    quantity: data.quantity ?? 0,
    balanceBefore: data.balanceBefore ?? 0,
    balanceAfter: data.balanceAfter ?? 0,

    // Personnel (dual witness mandatory)
    performedBy: data.performedBy || null,
    performedByName: data.performedByName || '',
    witnessedBy: data.witnessedBy || null,
    witnessedByName: data.witnessedByName || '',

    // Location
    facilityId: data.facilityId || null,
    wardId: data.wardId || null,
    storageLocation: data.storageLocation || '',

    // Discrepancy handling
    discrepancyResolution: data.discrepancyResolution || null,
    discrepancyInvestigationNotes: data.discrepancyInvestigationNotes || '',
    discrepancyResolvedBy: data.discrepancyResolvedBy || null,
    discrepancyResolvedAt: data.discrepancyResolvedAt || null,

    notes: data.notes || '',
    timestamp: data.timestamp || new Date().toISOString(),
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
    createdBy: data.createdBy || null,
  };
};

export const createS8DrugSummary = (data = {}) => {
  return {
    id: data.id || null,
    drugName: data.drugName || '',
    genericName: data.genericName || '',
    strength: data.strength || '',
    form: data.form || '',
    currentBalance: data.currentBalance ?? 0,
    storageLocation: data.storageLocation || '',
    batchNumber: data.batchNumber || '',
    expiryDate: data.expiryDate || null,
    lastCountDate: data.lastCountDate || null,
    lastCountBalance: data.lastCountBalance ?? 0,
    nextCountDue: data.nextCountDue || null,
    hasVariance: data.hasVariance ?? false,
    facilityId: data.facilityId || null,
    wardId: data.wardId || null,
    createdAt: data.createdAt || new Date().toISOString(),
    updatedAt: data.updatedAt || new Date().toISOString(),
  };
};

export const validateS8RegisterEntry = (entry) => {
  const errors = [];

  if (!entry.drugName) {
    errors.push('Drug name is required');
  }

  if (!entry.transactionType || !Object.values(S8_TRANSACTION_TYPE).includes(entry.transactionType)) {
    errors.push('Valid transaction type is required');
  }

  if (entry.quantity == null || entry.quantity < 0) {
    errors.push('Quantity must be a non-negative number');
  }

  if (entry.balanceBefore == null || entry.balanceBefore < 0) {
    errors.push('Balance before must be a non-negative number');
  }

  if (entry.balanceAfter == null || entry.balanceAfter < 0) {
    errors.push('Balance after must be a non-negative number');
  }

  if (!entry.performedBy) {
    errors.push('Performed by (staff ID) is required');
  }

  if (!entry.witnessedBy) {
    errors.push('Witness (staff ID) is required — dual witness is mandatory for S8');
  }

  if (entry.performedBy && entry.witnessedBy && entry.performedBy === entry.witnessedBy) {
    errors.push('Witness must be a different person from the performer');
  }

  // Balance reconciliation check
  const balanceTypes = {
    [S8_TRANSACTION_TYPE.RECEIVED]: 'increase',
    [S8_TRANSACTION_TYPE.TRANSFERRED_IN]: 'increase',
    [S8_TRANSACTION_TYPE.ADMINISTERED]: 'decrease',
    [S8_TRANSACTION_TYPE.WASTED]: 'decrease',
    [S8_TRANSACTION_TYPE.RETURNED]: 'decrease',
    [S8_TRANSACTION_TYPE.TRANSFERRED_OUT]: 'decrease',
  };

  const direction = balanceTypes[entry.transactionType];
  if (direction === 'increase') {
    const expected = entry.balanceBefore + entry.quantity;
    if (entry.balanceAfter !== expected) {
      errors.push(`Balance after (${entry.balanceAfter}) should equal balance before (${entry.balanceBefore}) + quantity (${entry.quantity}) = ${expected}`);
    }
  } else if (direction === 'decrease') {
    const expected = entry.balanceBefore - entry.quantity;
    if (entry.balanceAfter !== expected) {
      errors.push(`Balance after (${entry.balanceAfter}) should equal balance before (${entry.balanceBefore}) - quantity (${entry.quantity}) = ${expected}`);
    }
  }

  if (entry.transactionType === S8_TRANSACTION_TYPE.DISCREPANCY && !entry.notes) {
    errors.push('Notes are required when recording a discrepancy');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};
